<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import firebase from "firebase/app";
import "firebase/firestore";
import Swal from "sweetalert2";
import { registrar, ACCIONES } from "@/services/bitacora";
import { empleadoComputed } from "@/state/helpers";
import {
    estadoDe,
    estaVencido,
    estaActivo,
    vencimientoTexto,
    eventosPorCerrar,
} from "@/services/eventos";

export default {
    page: {
        title: "Actividades",
        meta: [{ name: "description", content: appConfig.description }],
    },
    components: {
        Layout,
    },
    data() {
        return {
            title: "Actividades",
            items: [
                { text: "Actividades", active: true },
            ],
            eventos: [],
            vacantes: [],
            searchQuery: "",
            eventosFields: [
                { key: 'nombre', label: 'Nombre del Evento', sortable: true },
                { key: 'comercio', label: 'Comercio', sortable: true },
                { key: 'vence', label: 'Finaliza' },
                { key: 'estado', label: 'Estado' },
                { key: 'actions', label: 'Detalles' }
            ],
            vacantesFields: [
                { key: 'tituloPuesto', label: 'Puesto', sortable: true },
                { key: 'nombreEmpresa', label: 'Empresa', sortable: true },
                { key: 'departamento', label: 'Departamento' },
                { key: 'estado', label: 'Estado' },
                { key: 'actions', label: 'Detalles' }
            ],
            showEventoModal: false,
            selectedEvento: null,
            showVacanteModal: false,
            selectedVacante: null,
            /** Cuántos eventos cerró el cierre automático en esta carga. */
            cerradosAutomaticamente: 0,
        };
    },
    computed: {
        ...empleadoComputed,
        filteredEventos() {
            if (!this.searchQuery) return this.eventos;
            const lowerCaseQuery = this.searchQuery.toLowerCase();
            return this.eventos.filter(e => (e.nombre && e.nombre.toLowerCase().includes(lowerCaseQuery)) || (e.comercio && e.comercio.toLowerCase().includes(lowerCaseQuery)));
        },
        filteredVacantes() {
            if (!this.searchQuery) return this.vacantes;
            const q = this.searchQuery.toLowerCase();
            return this.vacantes.filter(v => (v.tituloPuesto && v.tituloPuesto.toLowerCase().includes(q)) || (v.nombreEmpresa && v.nombreEmpresa.toLowerCase().includes(q)));
        },
        // Cada pestaña se parte en dos: lo que sigue vigente y lo que ya pasó.
        eventosActivos() {
            return this.filteredEventos.filter(e => estaActivo(e));
        },
        eventosPasados() {
            return this.filteredEventos.filter(e => !estaActivo(e));
        },
        vacantesActivas() {
            return this.filteredVacantes.filter(v => !!v.active);
        },
        vacantesInactivas() {
            return this.filteredVacantes.filter(v => !v.active);
        },
    },
    methods: {
        fetchData() {
            firebase.firestore().collection("queplanhoy").orderBy("creadoEn", "desc").get()
                .then(querySnapshot => {
                    this.eventos = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    return this.cerrarEventosVencidos();
                });

            firebase.firestore().collection("vacantes").orderBy("fechaPublicacion", "desc").get()
                .then(querySnapshot => {
                    this.vacantes = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                });
        },

        /**
         * Marca como inactivos los eventos cuya fecha y hora de término ya
         * pasaron, para que la app los trate como pasados. Se ejecuta al cargar
         * la vista y es idempotente: sólo toca los que siguen activos.
         */
        async cerrarEventosVencidos() {
            this.cerradosAutomaticamente = 0;
            if (!this.puedeEditar) return;

            const pendientes = eventosPorCerrar(this.eventos);
            if (!pendientes.length) return;

            const db = firebase.firestore();
            const lote = db.batch();
            pendientes.forEach((evento) => {
                lote.update(db.collection("queplanhoy").doc(evento.id), { activo: false });
            });

            try {
                await lote.commit();
                pendientes.forEach((evento) => {
                    // $set y no asignación directa: los eventos creados desde la app
                    // no traen `activo`, y Vue 2 no reacciona a propiedades nuevas.
                    this.$set(evento, "activo", false);
                    registrar({
                        accion: ACCIONES.DESACTIVAR,
                        coleccion: "queplanhoy",
                        docId: evento.id,
                        descripcion: `${evento.nombre} — cierre automático por fecha vencida`,
                        antes: { activo: true },
                        despues: { activo: false },
                    });
                });
                this.eventos = [...this.eventos];
                this.cerradosAutomaticamente = pendientes.length;
            } catch (error) {
                console.error("[actividades] no se pudieron cerrar los eventos vencidos:", error);
            }
        },

        // Métodos para Eventos
        estadoEvento(evento) {
            return estadoDe(evento);
        },
        eventoVencido(evento) {
            return estaVencido(evento);
        },
        eventoActivo(evento) {
            return estaActivo(evento);
        },
        vencimientoEvento(evento) {
            return vencimientoTexto(evento);
        },
        getEventoRowVariant(item) {
            if (!item) return;
            return estadoDe(item).filaVariante;
        },

        /** Interruptor de la tabla: no requiere abrir el detalle. */
        toggleEventoDesdeTabla(evento, nuevoEstado) {
            this.selectedEvento = evento;
            this.updateEventoStatus(nuevoEstado);
        },
        toggleVacanteDesdeTabla(vacante, nuevoEstado) {
            this.selectedVacante = vacante;
            this.updateVacanteStatus(nuevoEstado);
        },
        openEventoModal(evento) {
            this.selectedEvento = evento;
            this.showEventoModal = true;
        },
        updateEventoStatus(newStatus) {
            const eventoId = this.selectedEvento.id;
            firebase.firestore().collection("queplanhoy").doc(eventoId).update({ activo: newStatus })
                .then(() => registrar({
                    accion: newStatus ? ACCIONES.ACTIVAR : ACCIONES.DESACTIVAR,
                    coleccion: "queplanhoy",
                    docId: eventoId,
                    descripcion: this.selectedEvento.nombre,
                    antes: { activo: !newStatus },
                    despues: { activo: newStatus },
                }))
                .then(() => {
                    const index = this.eventos.findIndex(e => e.id === eventoId);
                    if (index !== -1) this.$set(this.eventos[index], "activo", newStatus);
                    this.$set(this.selectedEvento, "activo", newStatus);
                    Swal.fire("¡Éxito!", `El evento ha sido ${newStatus ? 'activado' : 'desactivado'}.`, "success");
                }).catch(error => Swal.fire("Error", "No se pudo actualizar el evento.", "error"));
        },
        deleteEvento() {
            const eventoId = this.selectedEvento.id;
            const eventoNombre = this.selectedEvento.nombre;
            Swal.fire({
                title: `¿Estás seguro de eliminar "${eventoNombre}"?`,
                text: "Esta acción no se puede deshacer.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar'
            }).then((result) => {
                if (result.isConfirmed) {
                    const antes = this.selectedEvento;
                    firebase.firestore().collection("queplanhoy").doc(eventoId).delete().then(() => registrar({
                        accion: ACCIONES.ELIMINAR,
                        coleccion: "queplanhoy",
                        docId: eventoId,
                        descripcion: eventoNombre,
                        antes,
                    })).then(() => {
                        this.eventos = this.eventos.filter(e => e.id !== eventoId);
                        this.showEventoModal = false;
                        Swal.fire('¡Eliminado!', 'El evento ha sido eliminado.', 'success');
                    }).catch(error => Swal.fire("Error", "No se pudo eliminar el evento.", "error"));
                }
            });
        },

        // Métodos para Vacantes
        getVacanteRowVariant(item, type) {
            if (!item) return;
            return item.active ? 'table-success' : 'table-warning';
        },
        openVacanteModal(vacante) {
            this.selectedVacante = vacante;
            this.showVacanteModal = true;
        },
        updateVacanteStatus(newStatus) {
            const vacanteId = this.selectedVacante.id;
            firebase.firestore().collection("vacantes").doc(vacanteId).update({ active: newStatus })
                .then(() => registrar({
                    accion: newStatus ? ACCIONES.ACTIVAR : ACCIONES.DESACTIVAR,
                    coleccion: "vacantes",
                    docId: vacanteId,
                    descripcion: this.selectedVacante.tituloPuesto,
                    antes: { active: !newStatus },
                    despues: { active: newStatus },
                }))
                .then(() => {
                    const index = this.vacantes.findIndex(v => v.id === vacanteId);
                    if (index !== -1) this.$set(this.vacantes[index], "active", newStatus);
                    this.$set(this.selectedVacante, "active", newStatus);
                    Swal.fire("¡Éxito!", `La vacante ha sido ${newStatus ? 'activada' : 'desactivada'}.`, "success");
                }).catch(error => Swal.fire("Error", "No se pudo actualizar la vacante.", "error"));
        },
        deleteVacante() {
            const vacanteId = this.selectedVacante.id;
            const vacanteTitulo = this.selectedVacante.tituloPuesto;
            Swal.fire({
                title: `¿Estás seguro de eliminar "${vacanteTitulo}"?`,
                text: "Esta acción no se puede deshacer.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar', cancelButtonText: 'Cancelar'
            }).then((result) => {
                if (result.isConfirmed) {
                    const antes = this.selectedVacante;
                    firebase.firestore().collection("vacantes").doc(vacanteId).delete().then(() => registrar({
                        accion: ACCIONES.ELIMINAR,
                        coleccion: "vacantes",
                        docId: vacanteId,
                        descripcion: vacanteTitulo,
                        antes,
                    })).then(() => {
                        this.vacantes = this.vacantes.filter(v => v.id !== vacanteId);
                        this.showVacanteModal = false;
                        Swal.fire('¡Eliminada!', 'La vacante ha sido eliminada.', 'success');
                    }).catch(error => Swal.fire("Error", "No se pudo eliminar la vacante.", "error"));
                }
            });
        }
    },
    mounted() {
        this.fetchData();
    },
};
</script>

<template>
    <Layout>
        <div class="mb-4">
            <img :src="require('@/assets/images/recnet-header.png')" alt="Sesiones de reconstrucción"
                class="img-fluid w-100 rounded shadow-sm"
                style="max-height: 260px; object-fit: cover; object-position: center;" />
        </div>

        <div class="row">
            <div class="col-12">
                <div class="card">
                    <div class="card-body">
                        <b-form-group label="Buscar..." label-for="search-input">
                            <b-form-input id="search-input" v-model="searchQuery" type="search"
                                placeholder="Escribe para buscar..."></b-form-input>
                        </b-form-group>

                        <b-tabs content-class="mt-4" fill>
                            <b-tab active>
                                <template #title><i class="mdi mdi-calendar-star"></i> Eventos ({{
                                    filteredEventos.length }})</template>
                                <b-alert :show="cerradosAutomaticamente > 0" variant="info" class="mb-3">
                                    <i class="mdi mdi-information-outline"></i>
                                    Se marcaron {{ cerradosAutomaticamente }} evento(s) como inactivos porque su fecha
                                    de término ya pasó.
                                </b-alert>

                                <!-- Activos -->
                                <h5 class="seccion-titulo">
                                    <i class="mdi mdi-check-circle text-success"></i>
                                    Activos ({{ eventosActivos.length }})
                                </h5>
                                <b-table responsive hover :items="eventosActivos" :fields="eventosFields"
                                    :tbody-tr-class="getEventoRowVariant" show-empty
                                    empty-text="No hay eventos activos.">
                                    <template #cell(vence)="row">
                                        {{ vencimientoEvento(row.item) }}
                                    </template>
                                    <template #cell(estado)="row">
                                        <div class="d-flex align-items-center">
                                            <b-badge :variant="estadoEvento(row.item).variante" class="mr-2">
                                                {{ estadoEvento(row.item).texto }}
                                            </b-badge>
                                            <b-form-checkbox v-if="puedeEditar && !eventoVencido(row.item)" switch
                                                class="mb-0" :checked="eventoActivo(row.item)"
                                                @change="toggleEventoDesdeTabla(row.item, $event)" />
                                        </div>
                                    </template>
                                    <template #cell(actions)="row">
                                        <b-button size="sm" variant="primary" @click="openEventoModal(row.item)"><i
                                                class="mdi mdi-eye"></i> Ver</b-button>
                                    </template>
                                </b-table>

                                <!-- Ya pasaron -->
                                <h5 class="seccion-titulo mt-4">
                                    <i class="mdi mdi-history text-muted"></i>
                                    Ya pasaron ({{ eventosPasados.length }})
                                </h5>
                                <b-table responsive hover :items="eventosPasados" :fields="eventosFields"
                                    :tbody-tr-class="getEventoRowVariant" show-empty
                                    empty-text="No hay eventos pasados.">
                                    <template #cell(vence)="row">
                                        {{ vencimientoEvento(row.item) }}
                                    </template>
                                    <template #cell(estado)="row">
                                        <div class="d-flex align-items-center">
                                            <b-badge :variant="estadoEvento(row.item).variante" class="mr-2">
                                                {{ estadoEvento(row.item).texto }}
                                            </b-badge>
                                            <b-form-checkbox v-if="puedeEditar && !eventoVencido(row.item)" switch
                                                class="mb-0" :checked="eventoActivo(row.item)"
                                                @change="toggleEventoDesdeTabla(row.item, $event)" />
                                        </div>
                                    </template>
                                    <template #cell(actions)="row">
                                        <b-button size="sm" variant="primary" @click="openEventoModal(row.item)"><i
                                                class="mdi mdi-eye"></i> Ver</b-button>
                                    </template>
                                </b-table>
                            </b-tab>

                            <b-tab>
                                <template #title><i class="mdi mdi-briefcase-search"></i> Vacantes ({{
                                    filteredVacantes.length }})</template>

                                <!-- Activas -->
                                <h5 class="seccion-titulo">
                                    <i class="mdi mdi-check-circle text-success"></i>
                                    Activas ({{ vacantesActivas.length }})
                                </h5>
                                <b-table responsive hover :items="vacantesActivas" :fields="vacantesFields"
                                    :tbody-tr-class="getVacanteRowVariant" show-empty
                                    empty-text="No hay vacantes activas.">
                                    <template #cell(estado)="row">
                                        <div class="d-flex align-items-center">
                                            <b-badge :variant="row.item.active ? 'success' : 'secondary'" class="mr-2">
                                                {{ row.item.active ? 'Activa' : 'Inactiva' }}
                                            </b-badge>
                                            <b-form-checkbox v-if="puedeEditar" switch class="mb-0"
                                                :checked="!!row.item.active"
                                                @change="toggleVacanteDesdeTabla(row.item, $event)" />
                                        </div>
                                    </template>
                                    <template #cell(actions)="row">
                                        <b-button size="sm" variant="primary" @click="openVacanteModal(row.item)"><i
                                                class="mdi mdi-eye"></i> Ver</b-button>
                                    </template>
                                </b-table>

                                <!-- Ya pasaron / inactivas -->
                                <h5 class="seccion-titulo mt-4">
                                    <i class="mdi mdi-history text-muted"></i>
                                    Ya pasaron ({{ vacantesInactivas.length }})
                                </h5>
                                <b-table responsive hover :items="vacantesInactivas" :fields="vacantesFields"
                                    :tbody-tr-class="getVacanteRowVariant" show-empty
                                    empty-text="No hay vacantes pasadas.">
                                    <template #cell(estado)="row">
                                        <div class="d-flex align-items-center">
                                            <b-badge :variant="row.item.active ? 'success' : 'secondary'" class="mr-2">
                                                {{ row.item.active ? 'Activa' : 'Inactiva' }}
                                            </b-badge>
                                            <b-form-checkbox v-if="puedeEditar" switch class="mb-0"
                                                :checked="!!row.item.active"
                                                @change="toggleVacanteDesdeTabla(row.item, $event)" />
                                        </div>
                                    </template>
                                    <template #cell(actions)="row">
                                        <b-button size="sm" variant="primary" @click="openVacanteModal(row.item)"><i
                                                class="mdi mdi-eye"></i> Ver</b-button>
                                    </template>
                                </b-table>
                            </b-tab>
                        </b-tabs>
                    </div>
                </div>
            </div>
        </div>

        <b-modal v-model="showEventoModal" size="lg" centered hide-footer
            :title="selectedEvento ? selectedEvento.nombre : ''">
            <div v-if="selectedEvento" class="evento-card">
                <b-row>
                    <b-col md="7">
                        <div class="evento-titulo">
                            <h2>{{ selectedEvento.nombre }}</h2>
                            <p class="comercio">por <strong>{{ selectedEvento.comercio }}</strong></p>
                            <div :class="['estado-badge', selectedEvento.activo ? 'activo' : 'inactivo']">
                                <i
                                    :class="['mdi', selectedEvento.activo ? 'mdi-check-circle' : 'mdi-close-circle']"></i>
                                {{ selectedEvento.activo ? 'Activo' : 'Inactivo' }}
                            </div>
                        </div>
                        <hr>
                        <p class="descripcion">{{ selectedEvento.descripcion }}</p>
                        <hr>
                        <div class="evento-detalles">
                            <div class="detalle-item">
                                <i class="mdi mdi-calendar-month"></i>
                                <span><strong>Fechas:</strong> Del {{ selectedEvento.fechaInicio }} al {{
                                    selectedEvento.fechaFin }}</span>
                            </div>
                            <div class="detalle-item">
                                <i class="mdi mdi-clock-outline"></i>
                                <span><strong>Horario:</strong> De {{ selectedEvento.horaInicio }} a {{
                                    selectedEvento.horaFin
                                    }}</span>
                            </div>
                            <div class="detalle-item">
                                <i class="mdi mdi-account-circle-outline"></i>
                                <span><strong>Publicado por:</strong> {{ selectedEvento.correoUsuario }}</span>
                            </div>
                        </div>
                    </b-col>
                    <b-col md="5">
                        <b-img :src="selectedEvento.imagenUrl" fluid thumbnail alt="Imagen del evento"></b-img>
                    </b-col>
                </b-row>
            </div>
            <hr>
            <div class="d-flex justify-content-end">
                <b-button v-if="puedeEditar" variant="danger" class="mr-2"
                    @click="deleteEvento">Eliminar Evento</b-button>
                <b-button v-if="puedeEditar && selectedEvento && eventoActivo(selectedEvento)" variant="warning"
                    class="mr-2" @click="updateEventoStatus(false)">Desactivar</b-button>
                <b-button
                    v-if="puedeEditar && selectedEvento && !eventoActivo(selectedEvento) && !eventoVencido(selectedEvento)"
                    variant="success" class="mr-2" @click="updateEventoStatus(true)">Activar</b-button>
                <b-button variant="secondary" @click="showEventoModal = false">Cerrar</b-button>
            </div>
        </b-modal>

        <b-modal v-model="showVacanteModal" size="lg" centered hide-footer :title="'Detalles de la Vacante'">
            <div v-if="selectedVacante" class="evento-card">

                <div class="evento-titulo">
                    <h2>{{ selectedVacante.tituloPuesto }}</h2>
                    <p class="comercio">para el comercio <strong>{{ selectedVacante.nombreEmpresa }}</strong></p>
                </div>
                <div :class="['estado-badge', selectedVacante.active ? 'activo' : 'inactivo']">
                    <i :class="['mdi', selectedVacante.active ? 'mdi-check-circle' : 'mdi-close-circle']"></i>
                    {{ selectedVacante.active ? 'Activa' : 'Inactiva' }}
                </div>
                <hr>
                <div class="evento-detalles">
                    <div class="detalle-item">
                        <i class="mdi mdi-office-building"></i>
                        <span><strong>Departamento:</strong> {{ selectedVacante.departamento }}</span>
                    </div>
                    <div class="detalle-item">
                        <i class="mdi mdi-cash-multiple"></i>
                        <span><strong>Rango Salarial:</strong> {{ selectedVacante.rangoSalarial }}</span>
                    </div>
                    <div class="detalle-item">
                        <i class="mdi mdi-file-document-edit-outline"></i>
                        <span><strong>Tipo de Contrato:</strong> {{ selectedVacante.tipoContrato }}</span>
                    </div>
                    <div class="detalle-item">
                        <i class="mdi mdi-star-circle-outline"></i>
                        <span><strong>Beneficios:</strong> {{ selectedVacante.beneficios }}</span>
                    </div>
                    <div class="detalle-item">
                        <i class="mdi mdi-format-list-checks"></i>
                        <span><strong>Requisitos:</strong> {{ selectedVacante.requisitosIndispensables }}</span>
                    </div>
                    <div class="detalle-item">
                        <i class="mdi mdi-account-circle-outline"></i>
                        <span><strong>Publicado por:</strong> {{ selectedVacante.register_by }}</span>
                    </div>
                </div>
            </div>
            <hr>
            <div class="d-flex justify-content-end">
                <b-button v-if="puedeEditar" variant="danger" class="mr-2"
                    @click="deleteVacante">Eliminar Vacante</b-button>
                <b-button v-if="puedeEditar && selectedVacante && selectedVacante.active" variant="warning" class="mr-2"
                    @click="updateVacanteStatus(false)">Desactivar</b-button>
                <b-button v-if="puedeEditar && selectedVacante && !selectedVacante.active" variant="success" class="mr-2"
                    @click="updateVacanteStatus(true)">Activar</b-button>
                <b-button variant="secondary" @click="showVacanteModal = false">Cerrar</b-button>
            </div>
        </b-modal>
    </Layout>
</template>

<style scoped>
.seccion-titulo {
    font-size: 1.05rem;
    font-weight: 600;
    color: #495057;
    padding-bottom: 6px;
    margin-bottom: 12px;
    border-bottom: 2px solid #eef0f2;
}

.seccion-titulo .mdi {
    margin-right: 6px;
}

.evento-card {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.evento-titulo h2 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #333;
    margin-bottom: 4px;
}

.evento-titulo .comercio {
    font-size: 1.1rem;
    color: #555;
    margin-bottom: 1rem;
}

.descripcion {
    font-size: 1rem;
    color: #666;
}

.evento-card hr {
    border: 0;
    border-top: 1px solid #eee;
    margin: 1rem 0;
}

.evento-detalles {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.detalle-item {
    display: flex;
    align-items: center;
    color: #495057;
    font-size: 0.95rem;
}

.detalle-item .mdi {
    font-size: 22px;
    color: #007bff;
    margin-right: 12px;
    width: 22px;
}

.estado-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
}

.estado-badge.activo {
    background-color: #e6f9f0;
    color: #008a45;
}

.estado-badge.inactivo {
    background-color: #fff3cd;
    color: #856404;
}

.estado-badge .mdi {
    margin-right: 4px;
}
</style>